"use client";

import { IconBoltOutline18, IconDatabaseOutline18, IconGearOutline18 } from "nucleo-ui-outline-18";
import { useState } from "react";

// Helper to get fallback icon for connector type
const getIconForType = (type: string): React.ComponentType<{ className?: string }> => {
  const lowerType = type.toLowerCase();

  // Check for database-related types
  if (lowerType.includes("db") || lowerType.includes("database") || lowerType.includes("sql")) {
    return IconDatabaseOutline18;
  }

  // Check for API/webhook types
  if (lowerType.includes("api") || lowerType.includes("webhook") || lowerType.includes("http")) {
    return IconBoltOutline18;
  }

  return IconGearOutline18;
};

interface ConnectorLogoProps {
  type: string;
  name: string;
  logoUrl?: string; // Optional direct logo URL (e.g., from Pipedream connectors)
  size?: "sm" | "base" | "md" | "lg";
  noBorder?: boolean; // Hide border overlays
}

const sizeClasses = {
  sm: { container: "h-6 w-6", icon: "h-3 w-3", logoContainer: "h-6 w-6" },
  base: { container: "h-8 w-8", icon: "h-4 w-4", logoContainer: "h-10 w-10" },
  md: { container: "h-12 w-12", icon: "h-6 w-6", logoContainer: "h-14 w-14" },
  lg: { container: "h-16 w-16", icon: "h-8 w-8", logoContainer: "h-18 w-18" },
};

export function ConnectorLogo({ type, name, logoUrl, size = "md", noBorder = false }: ConnectorLogoProps) {
  // A single stage counter drives the fallback chain. Incrementing it on
  // each error advances to the next source regardless of URL values, which
  // correctly handles the case where logoUrl === overrideUrl (URL collision).
  const [stage, setStage] = useState(0);

  const IconComponent = getIconForType(type);
  const normalizedType = type.includes(":") ? type.split(":")[1] : type;
  const aiIconUrl = `https://ai-icon.b3.fun/logo/${encodeURIComponent(normalizedType.replace(/\s+/g, "_"))}`;

  // Build an ordered, deduplicated source list so identical adjacent URLs
  // don't occupy two slots (a single failure skips past them correctly).
  const rawSources = [logoUrl, aiIconUrl].filter(Boolean) as string[];
  const sources = rawSources.filter((url, i) => rawSources.indexOf(url) === i);

  const logoSrc = sources[stage] ?? null;

  const handleError = () => setStage(s => s + 1);

  const sizes = sizeClasses[size];
  const roundedClass = size === "sm" ? "rounded-md" : size === "base" ? "rounded-lg" : "rounded-xl";

  // Show fallback icon when all sources are exhausted
  if (!logoSrc) {
    return (
      <div
        className={`flex ${sizes.container} items-center justify-center ${roundedClass} bg-blue-100 dark:bg-blue-500/10`}
      >
        <IconComponent className={`${sizes.icon} text-blue-600 dark:text-blue-400`} />
      </div>
    );
  }

  return (
    <div
      className={`relative flex ${sizes.logoContainer} items-center justify-center overflow-hidden ${roundedClass} shadow-ds-float-mid bg-white`}
    >
      <img
        src={logoSrc}
        alt={`${name} logo`}
        loading="lazy"
        className="h-full w-full object-contain"
        onError={handleError}
      />
      {!noBorder && (
        <>
          <div
            className={`absolute inset-0 ${roundedClass} border-ds-neutral-700/20 shadow-ds-input-inset dark:border-ds-neutral-200/20 border`}
          ></div>
          {size !== "sm" && (
            <>
              <div
                className={`absolute bottom-0 left-0 right-0 top-[1px] ${roundedClass} border-t border-white/30 dark:border-white/10`}
              ></div>
              <div
                className={`absolute bottom-[1px] left-0 right-0 top-0 ${roundedClass} border-b border-black/10 dark:border-black/10`}
              ></div>
            </>
          )}
        </>
      )}
    </div>
  );
}
